import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useNotification } from '../hooks/useNotification';
import { useUserStore } from '../stores/useUserStore';
import { staffProfileService } from '../services/staffProfileService';
import { nekoStaff } from 'public-contracts';
import { ModernStaffProfileEditDialog } from '../staff/ModernStaffProfileEditDialog';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Plus,
  Edit,
  Trash2,
  Users,
  MapPin,
  Building,
  DollarSign,
  MoreHorizontal,
  User,
  Briefcase,
} from 'lucide-react';

type StaffProfile = nekoStaff.StaffProfile;

const ModernStaffProfileList: React.FC = () => {
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const navigate = useNavigate();
  const { user, fetchUser } = useUserStore();

  const [staffProfiles, setStaffProfiles] = useState<StaffProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<StaffProfile | null>(null);

  // Ensure user data is loaded
  useEffect(() => {
    if (!user) {
      fetchUser();
    }
  }, [user, fetchUser]);

  // Load real data
  useEffect(() => {
    if (user?.id) {
      fetchStaffProfiles();
    }
  }, [user?.id]);

  const fetchStaffProfiles = async () => {
    try {
      setLoading(true);
      setError(null);

      // Only fetch the logged-in user's profile
      if (!user?.id) {
        setError(translate('pos.staff.profile.user_not_found', 'User not found'));
        return;
      }

      try {
        // Check for ALL profiles (to detect duplicates)
        const allProfiles = await staffProfileService.getAllStaffProfilesByUserId(
          String(user.id)
        );
        
        if (allProfiles.length === 0) {
          // No profile exists, create one automatically
          console.log('Staff profile not found, creating new profile...');
          try {
            const newProfile = await staffProfileService.createStaffProfile({
              userId: String(user.id),
              address: '',
              postionId: '',
              departmentId: '',
              ratePerHour: 0,
              ratePerHourCurrency: '$',
              sex: '',
            });
            setStaffProfiles([newProfile]);
            notify(
              translate(
                'pos.staff.profile.profile_created',
                'Staff profile created successfully'
              ),
              { type: 'success' }
            );
          } catch (createError: any) {
            // Check if error is due to duplicate (unique constraint violation)
            if (createError?.message?.includes('duplicate') || createError?.code === 11000) {
              // Profile was created by another request, fetch it
              const existingProfiles = await staffProfileService.getAllStaffProfilesByUserId(
                String(user.id)
              );
              if (existingProfiles.length > 0) {
                setStaffProfiles([existingProfiles[0]]);
              } else {
                throw createError;
              }
            } else {
              console.error('Error creating staff profile:', createError);
              setError(
                translate(
                  'pos.staff.profile.profile_create_failed',
                  'Failed to create your profile'
                )
              );
              notify(
                translate(
                  'pos.staff.profile.profile_create_failed',
                  'Failed to create your profile'
                ),
                { type: 'error' }
              );
            }
          }
        } else {
          // Profile(s) exist - warn if duplicates found
          if (allProfiles.length > 1) {
            console.warn(
              `⚠️ Found ${allProfiles.length} duplicate staff profiles for user ${user.id}`,
              allProfiles.map(p => ({ id: p.id, createdAt: (p as any).createdAt }))
            );
            notify(
              translate(
                'pos.staff.profile.duplicate_profiles_detected',
                `Warning: Found ${allProfiles.length} profiles. Please contact an administrator to merge them.`
              ),
              { type: 'warning' }
            );
          }
          // Show the most recent profile
          const sortedProfiles = allProfiles.sort((a, b) => {
            const aDate = new Date((a as any).createdAt || 0).getTime();
            const bDate = new Date((b as any).createdAt || 0).getTime();
            return bDate - aDate;
          });
          setStaffProfiles([sortedProfiles[0]]);
        }
      } catch (fetchError: any) {
        console.error('Error fetching staff profile:', fetchError);
        setError(
          translate(
            'pos.staff.profile.profile_load_failed',
            'Failed to load your profile'
          )
        );
      }
    } catch (err) {
      console.error('Error fetching staff profile:', err);
      setError(
        translate(
          'pos.staff.profile.profile_load_failed',
          'Failed to load your profile'
        )
      );
      notify(
        translate(
          'pos.staff.profile.profile_load_failed',
          'Failed to load your profile'
        ),
        { type: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(translate('pos.common.message.delete_content'))) return;

    try {
      await staffProfileService.deleteStaffProfile(id);
      setStaffProfiles((prev) => prev.filter((item) => item.id !== id));
      notify(
        translate(
          'pos.staff.profile.profile_deleted',
          'Staff profile deleted successfully'
        ),
        { type: 'success' }
      );
    } catch (err) {
      console.error('Error deleting staff profile:', err);
      notify(
        translate(
          'pos.staff.profile.profile_delete_failed',
          'Failed to delete staff profile'
        ),
        { type: 'error' }
      );
    }
  };

  // No filtering needed since we only show the current user's profile

  // Helper functions removed since we only show one profile

  if (loading) {
    return (
      <div className="space-y-6 py-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 py-6">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <Button onClick={fetchStaffProfiles}>
            {translate('pos.staff.profile.retry', 'Retry')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 py-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {translate('pos.staff.profile.title', 'My Profile')}
          </h1>
          <p className="text-muted-foreground mt-2">
            {translate(
              'pos.staff.profile.subtitle',
              'View and manage your personal profile information'
            )}
          </p>
        </div>
        {staffProfiles.length > 0 && (
          <Button
            onClick={() => {
              setEditingProfile(staffProfiles[0]);
              setEditDialogOpen(true);
            }}
            variant="default"
            className="gap-2"
          >
            <Edit className="h-4 w-4" />
            {translate('pos.common.action.edit', 'Edit Profile')}
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {translate('pos.staff.profile.total_staff', 'Total Staff')}
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {staffProfiles?.length || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              {translate(
                'pos.staff.profile.active_staff_members',
                'Active staff members'
              )}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {translate('pos.staff.profile.department', 'Department')}
            </CardTitle>
            <Building className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {staffProfiles.length > 0
                ? staffProfiles[0].departmentId || 'N/A'
                : 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">
              {translate('pos.staff.profile.your_department', 'Your department')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {translate('pos.staff.profile.position', 'Position')}
            </CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {staffProfiles.length > 0
                ? staffProfiles[0].postionId || 'N/A'
                : 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">
              {translate('pos.staff.profile.your_position', 'Your position')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {translate('pos.staff.profile.avg_rate', 'Avg Rate')}
            </CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              $
              {staffProfiles && staffProfiles.length > 0
                ? (
                    staffProfiles.reduce(
                      (sum, p) => sum + (p.ratePerHour || 0),
                      0
                    ) / staffProfiles.length
                  ).toFixed(2)
                : '0.00'}
            </div>
            <p className="text-xs text-muted-foreground">
              {translate(
                'pos.staff.profile.average_hourly_rate',
                'Average hourly rate'
              )}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Search and filters removed since we only show the current user's profile */}

      {/* Profile Information */}
      {staffProfiles.length > 0 ? (
        <div className="grid gap-6 md:grid-cols-2">
          {/* Personal Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                {translate(
                  'pos.staff.profile.personal_information',
                  'Personal Information'
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {translate('pos.staff.profile.user_id', 'User ID')}
                  </label>
                  <p className="text-lg font-semibold">
                    <span data-sensitive-field="userId">
                    {staffProfiles[0].userId}
                    </span>
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {translate('pos.staff.profile.address', 'Address')}
                  </label>
                  <p className="text-lg font-semibold">
                    <span data-sensitive-field="address">
                    {staffProfiles[0].address}
                    </span>
                  </p>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {translate('pos.staff.profile.gender', 'Gender')}
                </label>
                <p className="text-lg font-semibold">
                  {staffProfiles[0].sex ||
                    translate(
                      'pos.staff.profile.not_specified',
                      'Not specified'
                    )}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Professional Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5" />
                {translate(
                  'pos.staff.profile.professional_information',
                  'Professional Information'
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {translate(
                    'pos.staff.profile.department_id',
                    'Department ID'
                  )}
                </label>
                <p className="text-lg font-semibold">
                  {staffProfiles[0].departmentId ||
                    translate(
                      'pos.staff.profile.not_assigned',
                      'Not assigned'
                    )}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {translate('pos.staff.profile.position_id', 'Position ID')}
                </label>
                <p className="text-lg font-semibold">
                  {staffProfiles[0].postionId ||
                    translate(
                      'pos.staff.profile.not_assigned',
                      'Not assigned'
                    )}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {translate('pos.staff.profile.hourly_rate', 'Hourly Rate')}
                </label>
                <p className="text-lg font-semibold">
                  {staffProfiles[0].ratePerHourCurrency || '$'}{' '}
                  <span data-sensitive-field="amount">
                  {staffProfiles[0].ratePerHour?.toFixed(2) || '0.00'}
                  </span>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <Card>
          <CardContent className="flex items-center justify-center py-8">
            <div className="text-center">
              <User className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {translate(
                  'pos.staff.profile.no_profile_found',
                  'No Profile Found'
                )}
              </h3>
              <p className="text-muted-foreground">
                {translate(
                  'pos.staff.profile.profile_not_available',
                  'Your staff profile information is not available.'
                )}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      <ModernStaffProfileEditDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingProfile(null);
        }}
        onSuccess={() => {
          setEditDialogOpen(false);
          setEditingProfile(null);
          fetchStaffProfiles(); // Refresh the profile data
        }}
        profile={editingProfile}
      />
    </div>
  );
};

export default ModernStaffProfileList;
